import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import useLazyInView from '../hooks/useLazyInView';
import {
  Alert,
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  Chip,
  Stack,
  Autocomplete,
  Divider,
  Avatar,
  Popover,
  Paper,
  Snackbar
} from '@mui/material';
import { ArrowDropDown, AssignmentInd as AssignmentIndIcon, Badge as BadgeIcon, CalendarMonth, FileDownload, Person as PersonIcon } from '@mui/icons-material';
import { useMediaQuery, useTheme, Tooltip } from '@mui/material';
import PageHeader from './PageHeader';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import {
  pageFilterActionButtonSx,
  pageFilterAutocompleteSx,
  pageFilterDatePickerSx,
  pageFilterFormControlSx,
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import {
  createTodayDateRange,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  isDefaultDateTimeRange,
} from '../utils/filterDefaults';
import { DateRangePicker } from 'react-date-range';
import { format, isSameDay } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { exportAbsentEmployees, getAbsentLogs, getPersonFaces, getFaceImage, getUniqueAuditNames, getAllPersonNames } from '../utils/api';
import { filterPersonNameOptions, formatPersonNameOptionLabel, sanitizeNameSearchInput } from '../utils/nameSearchInput';
import {
  employeeTypeFilterLabel,
  employeeTypeFilterToRoleParams,
  normalizeEmployeeTypeFilter,
} from '../utils/employeeTypeUi';
import { keyframes } from '@mui/system';
import { EmployeeNameHyperlink } from './EmployeeNameHyperlink';
import { buildPersonLinkMeta } from '../utils/personDetailsNavigation';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useImagePreview } from './ImagePreviewDialog';

const primary = '#40518A';
const ABSENT_TYPE_FILTER_DEFAULT = 'employee';

const parseDateParam = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeTimeParam = (value, fallback) => {
  if (!value || typeof value !== 'string' || !value.includes(':')) return fallback;
  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return fallback;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const createDateRangeFromParams = (startValue, endValue) => {
  const startDate = parseDateParam(startValue) || new Date();
  const endDate = parseDateParam(endValue) || startDate;
  return [{ startDate, endDate, key: 'selection' }];
};

/** Normalize API `person_type` to Employee | Vendor | Visitor. */
const normalizeAbsentPersonType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'vendor') return 'Vendor';
  if (normalized === 'visitor') return 'Visitor';
  return 'Employee';
};

/** Map API `person_type` into the employee_type shape used by person-detail links. */
const personTypeToEmployeeType = (personType) => {
  const label = normalizeAbsentPersonType(personType);
  if (label === 'Vendor') return { is_vendor: true };
  if (label === 'Visitor') return { is_visitor: true };
  return null;
};

const AbsentPersonTypeChip = ({ personType }) => {
  const label = normalizeAbsentPersonType(personType);
  const config = {
    Employee: {
      icon: <PersonIcon sx={{ fontSize: '0.95rem !important' }} />,
      bgcolor: '#eef2ff',
      color: '#3730a3',
    },
    Vendor: {
      icon: <AssignmentIndIcon sx={{ fontSize: '0.95rem !important' }} />,
      bgcolor: '#ecfeff',
      color: '#0e7490',
    },
    Visitor: {
      icon: <BadgeIcon sx={{ fontSize: '0.95rem !important' }} />,
      bgcolor: '#fdf4ff',
      color: '#86198f',
    },
  }[label];

  return (
    <Tooltip title={label} arrow>
      <Chip
        size="small"
        icon={config.icon}
        label={label}
        sx={{
          height: { xs: 20, sm: 22 },
          fontSize: { xs: '0.62rem', sm: '0.68rem' },
          fontWeight: 700,
          bgcolor: config.bgcolor,
          color: config.color,
          '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
        }}
      />
    </Tooltip>
  );
};

/** Tooltip with full text only when the content is visually truncated (...). */
const TruncatedWithTooltip = ({ title = '', children }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const node = textRef.current;
    if (!node) {
      setIsTruncated(false);
      return;
    }
    setIsTruncated(node.scrollWidth > node.clientWidth + 1);
  }, []);

  useEffect(() => {
    measure();
    const node = textRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, title, children]);

  const child = React.Children.only(children);
  return (
    <Tooltip title={title} arrow disableHoverListener={!isTruncated || !title}>
      {React.cloneElement(child, {
        ref: textRef,
        noWrap: true,
        sx: {
          ...(child.props?.sx || {}),
          display: 'block',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      })}
    </Tooltip>
  );
};

// Shimmer animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Shimmer effect styles
const shimmerStyles = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
};

// Attendance Card Skeleton Component
export const AttendanceCardSkeleton = () => (
  <Card 
    sx={{ 
      borderRadius: 3, 
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      {/* Header with avatar and name */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            ...shimmerStyles,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ width: '60%', height: 20, borderRadius: 1, mb: 0.5, ...shimmerStyles }} />
          <Box sx={{ width: '40%', height: 14, borderRadius: 1, ...shimmerStyles }} />
        </Box>
      </Stack>

      {/* Status chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ width: 70, height: 24, borderRadius: 12, ...shimmerStyles }} />
        <Box sx={{ width: 80, height: 24, borderRadius: 12, ...shimmerStyles }} />
        <Box sx={{ width: 90, height: 24, borderRadius: 12, ...shimmerStyles }} />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Recent Activity header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ width: 100, height: 12, borderRadius: 1, ...shimmerStyles }} />
        <Stack direction="row" spacing={0.5}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, ...shimmerStyles }} />
          <Box sx={{ width: 40, height: 28, borderRadius: 1, ...shimmerStyles }} />
          <Box sx={{ width: 28, height: 28, borderRadius: 1, ...shimmerStyles }} />
        </Stack>
      </Stack>

      {/* Activity tiles */}
      <Stack direction="row" spacing={1.2}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ flex: 1 }}>
            <Box sx={{ width: '100%', height: 100, borderRadius: 2, mb: 1, ...shimmerStyles }} />
            <Box sx={{ width: '70%', height: 14, borderRadius: 1, mb: 0.5, ...shimmerStyles }} />
            <Box sx={{ width: '50%', height: 12, borderRadius: 1, ...shimmerStyles }} />
          </Box>
        ))}
      </Stack>
    </CardContent>
  </Card>
);

const AbsentEmployeeCard = ({ employee, personThumbnail, onRequestPersonThumbnail }) => {
  const { openImagePreview } = useImagePreview();
  const lazyRef = useLazyInView(() => {
    if (employee?.person_id && typeof onRequestPersonThumbnail === 'function') {
      onRequestPersonThumbnail(employee.person_id);
    }
  }, { once: true });
  const personType = normalizeAbsentPersonType(employee?.person_type);
  const employeeType = personTypeToEmployeeType(personType);
  const personName = employee?.person_name || 'Unknown person';
  const thumbnail = employee?.person_id ? (personThumbnail || null) : null;

  const handleProfileImagePreview = (event) => {
    if (!thumbnail) return;
    event.preventDefault();
    event.stopPropagation();
    openImagePreview({
      src: thumbnail,
      title: personName || 'Profile image',
      alt: personName || 'Profile image',
      employeeName: personName,
      employeeId: employee?.employee_code || employee?.person_id || '',
      employeeImage: thumbnail,
      hideTitle: true,
    });
  };

  return (
    <Box
      ref={lazyRef}
      sx={{
        width: '100%',
        minWidth: 0,
      }}
    >
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          width: '100%',
          bgcolor: '#ffffff',
          color: '#fff',
          borderTop: '3px solid #ef4444',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.21)',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
            transform: 'translateY(-4px)',
            borderColor: '#ef4444',
          },
          '&:active': {
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.25, sm: 1.5 },
          flex: 1,
          p: { xs: 1.5, sm: 2 },
        }}
        >
          <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              src={thumbnail || undefined}
              alt={personName}
              imgProps={{
                'data-no-image-preview': 'true',
              }}
              onClick={handleProfileImagePreview}
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                fontWeight: 600,
                bgcolor: '#fecaca',
                color: '#991b1b',
                flexShrink: 0,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                cursor: thumbnail ? 'pointer' : 'default',
              }}
            >
              {(employee.person_name || employee.person_id || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <TruncatedWithTooltip title={employee.person_name || 'Unknown person'}>
                <Typography
                  variant="subtitle1"
                  color="text.primary"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  }}
                >
                  <EmployeeNameHyperlink
                    text={employee.person_name || 'Unknown person'}
                    employees={[buildPersonLinkMeta(
                      { ...employee, employee_type: employeeType },
                      { routeSource: 'facesPerson' },
                    )]}
                    style={{ display: 'inline' }}
                  />
                </Typography>
              </TruncatedWithTooltip>
              {employee.employee_code ? (
                <Tooltip title={employee.employee_code} arrow>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{
                      display: 'block',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    }}
                  >
                    {employee.employee_code}
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
          </Stack>
          <Box sx={{ mt: 'auto', pt: 0.25 }}>
            <AbsentPersonTypeChip personType={personType} />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const Unavailable = () => {
  const { confirm } = useConfirmDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // < 900px

  const initialPersonName = sanitizeNameSearchInput(searchParams.get('person_name') || '');
  const initialTypeFilter = normalizeEmployeeTypeFilter(
    searchParams.get('employee_type') || ABSENT_TYPE_FILTER_DEFAULT
  );
  const initialDateRange = createDateRangeFromParams(
    searchParams.get('start_date'),
    searchParams.get('end_date')
  );
  const initialStartTime = normalizeTimeParam(searchParams.get('start_time'), DEFAULT_START_TIME);
  const initialEndTime = normalizeTimeParam(searchParams.get('end_time'), DEFAULT_END_TIME);

  // Attendance state
  const [availableNames, setAvailableNames] = useState([]);
  const safeAvailableNames = Array.isArray(availableNames) ? availableNames : [];
  const [personThumbnails, setPersonThumbnails] = useState({});
  const personThumbnailsRef = useRef({});
  const thumbnailRequestsRef = useRef(new Set());
  // Absent state
  const [absentData, setAbsentData] = useState([]);
  const [absentPagination, setAbsentPagination] = useState({ total: 0, limit: 100, offset: 0, has_next: false, has_prev: false, total_pages: 1, current_page: 1 });
  const [absentLoading, setAbsentLoading] = useState(false);
  const [absentPersonName, setAbsentPersonName] = useState(initialPersonName);
  const [absentPersonNameInput, setAbsentPersonNameInput] = useState(initialPersonName);
  const [absentTypeFilter, setAbsentTypeFilter] = useState(initialTypeFilter);

  // Responsive Grid state for Absent cards
  const absentContainerRef = useRef(null);
  const [absentContainerWidth, setAbsentContainerWidth] = useState(window.innerWidth || 1200);
  const [absentCardsPerRow, setAbsentCardsPerRow] = useState(() => {
    const width = window.innerWidth || 1200;
    if (width < 600) return 2;
    if (width < 900) return 3;
    if (width < 1200) return 4;
    if (width < 1600) return 5;
    return 6;
  });

  // Update container width on window resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setAbsentContainerWidth(width);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!absentContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setAbsentContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(absentContainerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    // More cards per row => smaller card width
    const getCardsPerRow = () => {
      if (absentContainerWidth < 600) return 2;
      if (absentContainerWidth < 900) return 3;
      if (absentContainerWidth < 1200) return 4;
      if (absentContainerWidth < 1600) return 5;
      return 6;
    };
    setAbsentCardsPerRow(getCardsPerRow());
  }, [absentContainerWidth]);

  // Applied date/time (used for fetching + filter chip). Draft is edited in the picker until Apply.
  const [absentDateRange, setAbsentDateRange] = useState(initialDateRange);
  const [absentStartTime, setAbsentStartTime] = useState(initialStartTime);
  const [absentEndTime, setAbsentEndTime] = useState(initialEndTime);
  const [draftAbsentDateRange, setDraftAbsentDateRange] = useState(initialDateRange);
  const [draftAbsentStartTime, setDraftAbsentStartTime] = useState(initialStartTime);
  const [draftAbsentEndTime, setDraftAbsentEndTime] = useState(initialEndTime);
  const [absentDatePickerAnchor, setAbsentDatePickerAnchor] = useState(null);
  const absentDatePickerOpen = Boolean(absentDatePickerAnchor);

  const openAbsentDatePicker = useCallback((event) => {
    setDraftAbsentDateRange(absentDateRange);
    setDraftAbsentStartTime(absentStartTime);
    setDraftAbsentEndTime(absentEndTime);
    setAbsentDatePickerAnchor(event.currentTarget);
  }, [absentDateRange, absentStartTime, absentEndTime]);

  const closeAbsentDatePicker = useCallback(() => {
    setAbsentDatePickerAnchor(null);
  }, []);

  const applyAbsentDateFilters = useCallback(() => {
    setAbsentDateRange(draftAbsentDateRange);
    setAbsentStartTime(draftAbsentStartTime);
    setAbsentEndTime(draftAbsentEndTime);
    setAbsentDatePickerAnchor(null);
  }, [draftAbsentDateRange, draftAbsentStartTime, draftAbsentEndTime]);

  const [absentExportLoading, setAbsentExportLoading] = useState(false);
  const [absentExportMenuAnchor, setAbsentExportMenuAnchor] = useState(null);
  const absentExportMenuOpen = Boolean(absentExportMenuAnchor);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const show = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const applyAbsentPersonNameNow = useCallback((rawValue) => {
    const sanitizedValue = sanitizeNameSearchInput(rawValue || '');
    setAbsentPersonNameInput(sanitizedValue);
    setAbsentPersonName(sanitizedValue);
  }, []);

  const hasActiveFilters = useMemo(() => (
    !!(absentPersonName?.trim()) ||
    normalizeEmployeeTypeFilter(absentTypeFilter) !== ABSENT_TYPE_FILTER_DEFAULT ||
    !isDefaultDateTimeRange(absentDateRange, absentStartTime, absentEndTime)
  ), [absentDateRange, absentEndTime, absentPersonName, absentStartTime, absentTypeFilter]);

  // Persist applied filters in the URL (same pattern as Audit / Present).
  useEffect(() => {
    setSearchParams((prevParams) => {
      const params = new URLSearchParams(prevParams);

      if (absentPersonName) params.set('person_name', absentPersonName); else params.delete('person_name');

      if (absentTypeFilter && absentTypeFilter !== ABSENT_TYPE_FILTER_DEFAULT) {
        params.set('employee_type', absentTypeFilter);
      } else {
        params.delete('employee_type');
      }

      if (absentDateRange[0]?.startDate) {
        params.set('start_date', format(absentDateRange[0].startDate, 'yyyy-MM-dd'));
      } else {
        params.delete('start_date');
      }
      if (absentDateRange[0]?.endDate) {
        params.set('end_date', format(absentDateRange[0].endDate, 'yyyy-MM-dd'));
      } else {
        params.delete('end_date');
      }

      if (absentStartTime && absentStartTime !== DEFAULT_START_TIME) {
        params.set('start_time', absentStartTime);
      } else {
        params.delete('start_time');
      }
      if (absentEndTime && absentEndTime !== DEFAULT_END_TIME) {
        params.set('end_time', absentEndTime);
      } else {
        params.delete('end_time');
      }

      return params.toString() !== prevParams.toString() ? params : prevParams;
    }, { replace: true });
  }, [
    absentDateRange,
    absentEndTime,
    absentPersonName,
    absentStartTime,
    absentTypeFilter,
    setSearchParams,
  ]);

  useEffect(() => {
    const loadNames = async () => {
      try {
     
        const res = await getAllPersonNames();
        const names = res?.map((d)=>`${d.name} [${d.person_id}]`) || [];

        names.sort((a, b) => a.localeCompare(b));
        setAvailableNames(res || []);
      } catch (e) {
        console.error('Failed to load unique names:', e);
      }
    };
    loadNames();
  }, []);

  // Build absent date time range function
  const buildAbsentDateTimeRange = useCallback(() => {
    const startDate = absentDateRange[0]?.startDate || new Date();
    const endDate = absentDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    // Parse IST times (user input)
    const [istStartHour, istStartMinute] = parseTime(absentStartTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(absentEndTime, 23, 59);

    // IST is UTC+5:30, so we need to subtract 5:30 from IST time to get UTC
    // Create date objects representing IST time, then convert to UTC
    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const startDateTimeUTC = new Date(startDateIST.getTime());// - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      59
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const endDateTimeUTC = new Date(endDateIST.getTime());// - (5 * 60 + 30) * 60 * 1000);

    // Format as ISO string (yyyy-MM-ddTHH:mm format, backend will parse as UTC)
    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16), // Format: yyyy-MM-ddTHH:mm
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16), // Format: yyyy-MM-ddTHH:mm
    };
  }, [absentDateRange, absentStartTime, absentEndTime]);

  const loadPersonThumbnailIfNeeded = useCallback((personId) => {
    if (!personId) return;
    if (personThumbnailsRef.current[personId] !== undefined || thumbnailRequestsRef.current.has(personId)) {
      return;
    }
    thumbnailRequestsRef.current.add(personId);
    (async () => {
      try {
        const faces = await getPersonFaces(personId);
        const faceList = Array.isArray(faces) ? faces : [];
        const preferredFace = faceList.find((face) => !face.deleted_at) || faceList[0];
        if (preferredFace) {
          const blob = await getFaceImage(preferredFace.face_id);
          const dataUrl = await blobToDataUrl(blob);
          setPersonThumbnails((prev) => ({ ...prev, [personId]: dataUrl }));
        } else {
          setPersonThumbnails((prev) => ({ ...prev, [personId]: null }));
        }
      } catch (err) {
        console.error('Failed to load absent employee thumbnail:', err);
        setPersonThumbnails((prev) => ({ ...prev, [personId]: null }));
      } finally {
        thumbnailRequestsRef.current.delete(personId);
      }
    })();
  }, []);

  useEffect(() => {
    personThumbnailsRef.current = personThumbnails;
  }, [personThumbnails]);

  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const groupAbsentByDate = (absentEmployees) => {
    const groups = {};
    absentEmployees.forEach(employee => {
      const dateKey = employee.absent_date || employee.date || format(new Date(), 'yyyy-MM-dd');
      const dateLabel = format(new Date(dateKey), 'EEEE, MMMM dd, yyyy');
      
      if (!groups[dateKey]) {
        groups[dateKey] = {
          dateKey,
          dateLabel,
          employees: []
        };
      }
      groups[dateKey].employees.push(employee);
    });
    
    // Sort dates (most recent first)
    return Object.values(groups).sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  };

  // Absent data loader
  const fetchAbsentData = useCallback(async (limit = 10, offset = 0) => {
    try {
      setAbsentLoading(true);
      const { formattedStart, formattedEnd } = buildAbsentDateTimeRange();
      const roleParams = employeeTypeFilterToRoleParams(absentTypeFilter);

      const payload = {
        start_time: formattedStart,
        end_time: formattedEnd,
        limit,
        offset,
        sort_order: 'desc',
        person_name: absentPersonName.trim(),
        ...roleParams,
      };

      const res = await getAbsentLogs(payload);

      setAbsentData(res.absent_employees || []);
      setAbsentPagination(res.pagination || { total: 0, limit, offset, has_next: false, has_prev: false, total_pages: 1, current_page: 1 });
    } catch (e) {
      console.error(e);
      show('Failed to load unavailable records', 'error');
      setAbsentData([]);
      setAbsentPagination({ total: 0, limit, offset, has_next: false, has_prev: false, total_pages: 1, current_page: 1 });
    } finally {
      setAbsentLoading(false);
    }
  }, [absentPersonName, absentTypeFilter, buildAbsentDateTimeRange, show]);

  const handleClearAllFilters = useCallback(() => {
    setAbsentPersonName('');
    setAbsentPersonNameInput('');
    setAbsentTypeFilter(ABSENT_TYPE_FILTER_DEFAULT);
    const todayRange = createTodayDateRange();
    setAbsentDateRange(todayRange);
    setAbsentStartTime(DEFAULT_START_TIME);
    setAbsentEndTime(DEFAULT_END_TIME);
    setDraftAbsentDateRange(todayRange);
    setDraftAbsentStartTime(DEFAULT_START_TIME);
    setDraftAbsentEndTime(DEFAULT_END_TIME);
  }, []);

  // Load absent data when filters change (always reset to page 1)
  useEffect(() => {
    fetchAbsentData(absentPagination.limit, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absentPersonName, absentTypeFilter, absentDateRange, absentStartTime, absentEndTime]);

  const openAbsentExportMenu = (event) => {
    setAbsentExportMenuAnchor(event.currentTarget);
  };

  const closeAbsentExportMenu = () => {
    setAbsentExportMenuAnchor(null);
  };

  const handleExportAbsentEmployees = useCallback(async (formatType = 'csv') => {
    try {
      closeAbsentExportMenu();
      const normalizedFormat = String(formatType || 'csv').toLowerCase() === 'xlsx' ? 'xlsx' : 'csv';
      const approved = await confirm({
        title: 'Confirm Export',
        message: `Are you sure you want to export unavailable employees as ${normalizedFormat.toUpperCase()}?`,
        confirmLabel: `Export ${normalizedFormat.toUpperCase()}`,
        danger: false,
      });
      if (!approved) return;

      setAbsentExportLoading(true);
      const { formattedStart, formattedEnd } = buildAbsentDateTimeRange();
      const roleParams = employeeTypeFilterToRoleParams(absentTypeFilter);
      const safeFileName = 'absent_employees';

      const { blob, filename } = await exportAbsentEmployees({
        start_time: formattedStart,
        end_time: formattedEnd,
        person_name: (absentPersonName || '').trim(),
        sort_order: 'desc',
        export_format: normalizedFormat,
        file_name: safeFileName,
        ...roleParams,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fallbackExt = normalizedFormat === 'xlsx' ? 'xlsx' : 'csv';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = filename || `${safeFileName}_${timestamp}.${fallbackExt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      show(`Unavailable employees exported successfully as ${normalizedFormat.toUpperCase()}`, 'success');
    } catch (error) {
      console.error('Failed to export unavailable employees:', error);
      show(error?.message || 'Failed to export unavailable employees', 'error');
    } finally {
      setAbsentExportLoading(false);
    }
  }, [absentPersonName, absentTypeFilter, buildAbsentDateTimeRange, confirm, show]);
  
  const formatEnrolledPersonNameOption = useCallback((option) => {
    return formatPersonNameOptionLabel(option, { includeCode: true });
  }, []);
  const filterEnrolledPersonNameOptions = useCallback((options, state) => {
    return filterPersonNameOptions(options, state);
  }, []);

  return (
    <Container maxWidth="2200" disableGutters sx={{ px: 0 }}>
        <PageHeader
          title={"Unavailable"}
          subtitle={"View unavailable employees"}
          icon={null}
        />
    
        <Box>
            {/* Filters Section */}
            <Box sx={pageFiltersToolbarSx}>
            <Stack {...pageFiltersStackSx}>
                <Box sx={pageFiltersControlsSx}>
                <Autocomplete
                    size="small"
                    freeSolo
                    options={safeAvailableNames}
                    getOptionLabel={formatEnrolledPersonNameOption}
                    filterOptions={filterEnrolledPersonNameOptions}
                    value={absentPersonNameInput || null}
                    inputValue={absentPersonNameInput || ''}
                    onInputChange={(e, newValue) => {
                    const sanitizedValue = sanitizeNameSearchInput(newValue || '');
                    setAbsentPersonNameInput(sanitizedValue);
                    }}
                    onChange={(e, newValue) => {
                    const selectedValue =
                      typeof newValue === 'string'
                        ? newValue
                        : newValue?.name || '';

                    applyAbsentPersonNameNow(selectedValue);
                    }}  
                    renderInput={(params) => (
                      
                      <TextField
                        {...params}
                        placeholder=" Search by Name or person id"
                        onKeyDown={(e) => {
                          if (typeof params?.inputProps?.onKeyDown === 'function') {
                            params.inputProps.onKeyDown(e);
                          }
                          if (e.key !== 'Enter') return;
                          applyAbsentPersonNameNow(e.currentTarget.value || '');
                        }}
                      />
                    
                    )}
                    sx={pageFilterAutocompleteSx(280)}
                />

                <FormControl
                  size="small"
                  sx={pageFilterFormControlSx(150)}
                >
                  <InputLabel id="absent-type-filter-label" shrink>
                    Type
                  </InputLabel>
                  <Select
                    labelId="absent-type-filter-label"
                    value={absentTypeFilter}
                    label="Type"
                    onChange={(e) => setAbsentTypeFilter(normalizeEmployeeTypeFilter(e.target.value))}
                    displayEmpty
                    renderValue={employeeTypeFilterLabel}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="employee">Employee</MenuItem>
                    <MenuItem value="vendor">Vendor</MenuItem>
                    <MenuItem value="visitor">Visitor</MenuItem>
                  </Select>
                </FormControl>

                <Box
                    onClick={openAbsentDatePicker}
                    sx={pageFilterDatePickerSx(180)}
                >
                    <CalendarMonth fontSize="small" color="action" />
                    <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                    {isSameDay(absentDateRange[0].startDate, absentDateRange[0].endDate)
                        ? format(absentDateRange[0].startDate, 'MMM dd, yyyy')
                        : `${format(absentDateRange[0].startDate, 'MMM dd')} - ${format(absentDateRange[0].endDate, 'MMM dd, yyyy')}`}
                    </Typography>
                </Box>
                <Popover
                    open={absentDatePickerOpen}
                    anchorEl={absentDatePickerAnchor}
                    onClose={closeAbsentDatePicker}
                    anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                    }}
                    transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                    }}
                    slotProps={{
                    paper: {
                        sx: { maxWidth: '100vw', overflow: 'auto' }
                    }
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                    {/* Quick Select Presets */}
                    <Box sx={{
                        p: 2,
                        borderRight: { md: '1px solid' },
                        borderBottom: { xs: '1px solid', md: 'none' },
                        borderColor: 'divider',
                        minWidth: 140,
                    }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Quick Select
                        </Typography>
                        <Stack spacing={0.5}>
                        {[
                            { label: 'Today', days: 0 },
                            { label: 'Yesterday', days: -1 },
                            { label: 'Last 7 Days', days: -6 },
                            { label: 'Last 30 Days', days: -29 },
                        ].map((preset) => (
                            <Button
                            key={preset.label}
                            size="small"
                            onClick={() => {
                                const today = new Date();
                                const startDate = new Date(today);
                                startDate.setDate(today.getDate() + preset.days);
                                setDraftAbsentDateRange([{ startDate, endDate: today, key: 'selection' }]);
                            }}
                            sx={{
                                justifyContent: 'flex-start',
                                px: 1.5,
                                py: 0.75,
                                color: 'text.primary',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            >
                            {preset.label}
                            </Button>
                        ))}
                        </Stack>
                    </Box>

                    {/* Date Range Picker - Responsive */}
                    <Box sx={{
                        '& .rdrCalendarWrapper': {
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        },
                        '& .rdrMonth': {
                        width: { xs: '280px', sm: '300px' },
                        },
                    }}>
                        <DateRangePicker
                        ranges={draftAbsentDateRange}
                        onChange={(ranges) => setDraftAbsentDateRange([ranges.selection])}
                        moveRangeOnFirstSelection={false}
                        months={isMobile ? 1 : 2}
                        direction={isMobile ? 'vertical' : 'horizontal'}
                        rangeColors={[primary]}
                        staticRanges={[]}
                        inputRanges={[]}
                        maxDate={new Date()}
                        />
                        <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                            label="Start Time"
                            type="time"
                            size="small"
                            value={draftAbsentStartTime}
                            onChange={(e) => setDraftAbsentStartTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 60 }}
                            fullWidth
                        />
                        <TextField
                            label="End Time"
                            type="time"
                            size="small"
                            value={draftAbsentEndTime}
                            onChange={(e) => setDraftAbsentEndTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 60 }}
                            fullWidth
                        />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                        <Button
                            onClick={() => {
                            const todayRange = createTodayDateRange();
                            setDraftAbsentDateRange(todayRange);
                            setDraftAbsentStartTime(DEFAULT_START_TIME);
                            setDraftAbsentEndTime(DEFAULT_END_TIME);
                            }}
                            sx={{ textTransform: 'none' }}
                        >
                            Reset
                        </Button>
                        <Button onClick={closeAbsentDatePicker} variant="outlined" size="small">
                            Cancel
                        </Button>
                        <Button
                            onClick={applyAbsentDateFilters}
                            variant="contained"
                            size="small"
                        >
                            Apply
                        </Button>
                        </Box>
                    </Box>
                    </Box>
                </Popover>

                <ClearAllFiltersButton
                  onClear={handleClearAllFilters}
                  disabled={!hasActiveFilters}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                />
                </Box>
                <Box sx={pageFiltersActionsSx}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownload />}
                    endIcon={<ArrowDropDown />}
                    onClick={openAbsentExportMenu}
                    disabled={absentLoading || absentExportLoading}
                    sx={{
                      ...pageFilterActionButtonSx,
                      minWidth: { xs: '100%', lg: 160 },
                    }}
                  >
                    Export
                  </Button>
                  <Menu
                    anchorEl={absentExportMenuAnchor}
                    open={absentExportMenuOpen}
                    onClose={closeAbsentExportMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem onClick={() => handleExportAbsentEmployees('csv')}>Export in CSV</MenuItem>
                    <MenuItem onClick={() => handleExportAbsentEmployees('xlsx')}>Export in XLSX</MenuItem>
                  </Menu>
                </Box>
            </Stack>
            </Box>

            {/* Absent Data Display */}
            {absentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
            ) : absentData.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>No absent records found</Typography>
                <Typography variant="body2" color="text.secondary">
                Try adjusting the date range or filters to see results.
                </Typography>
            </Paper>
            ) : (
            <Box ref={absentContainerRef}>
                {/* Group absent employees by date */}
                {groupAbsentByDate(absentData).map((dateGroup) => (
                <Box key={dateGroup.dateKey} sx={{ mb: 4 }}>
                    {/* Date Header */}
                    <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    pb: 1,
                    borderBottom: '2px solid #e5e7eb'
                    }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                        fontWeight: 600, 
                        color: '#374151',
                        fontSize: { xs: '1rem', sm: '1.1rem' }
                        }}
                    >
                        {dateGroup.dateLabel}
                    </Typography>
                    <Chip
                        size="small"
                        label={`${dateGroup.employees.length} people unavailable`}
                        color="error"
                        variant="outlined"
                        sx={{ ml: 2, fontWeight: 500 }}
                    />
                    </Box>

                    {/* Employees Grid for this date */}
                    <Box>
                    {(() => {
                        const rows = [];
                        for (let i = 0; i < dateGroup.employees.length; i += absentCardsPerRow) {
                        rows.push(dateGroup.employees.slice(i, i + absentCardsPerRow));
                        }

                        return rows.map((row, rowIndex) => {
                        const rowItemCount = row.length;

                        return (
                            <Box
                            key={`absent-row-${dateGroup.dateKey}-${rowIndex}`}
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 2, sm: 2.5 },
                                mb: rowIndex < rows.length - 1 ? { xs: 2, sm: 2.5 } : 0,
                            }}
                            >
                            {row.map((employee) => (
                                <Box
                                key={employee.person_id}
                                sx={{
                                    flex: rowItemCount < absentCardsPerRow ? '0 0 auto' : 1,
                                    minWidth: 0,
                                    width: rowItemCount < absentCardsPerRow
                                    ? { xs: '100%', sm: `calc((100% - ${(absentCardsPerRow - 2.5) * 40}px) / ${absentCardsPerRow})` }
                                    : 'auto',
                                }}
                                >
                                <AbsentEmployeeCard
                                  employee={employee}
                                  personThumbnail={employee.person_id ? personThumbnails[employee.person_id] : null}
                                  onRequestPersonThumbnail={loadPersonThumbnailIfNeeded}
                                />
                                </Box>
                            ))}
                            </Box>
                        );
                        });
                    })()}
                    </Box>
                </Box>
                ))}

                {/* Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        Showing {absentPagination.total === 0 ? 0 : absentPagination.offset + 1}-{Math.min(absentPagination.offset + absentPagination.limit, absentPagination.total)} of {absentPagination.total} records
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Cards per page:</Typography>
                    <Select
                        size="small"
                        value={absentPagination.limit}
                        onChange={(e) => fetchAbsentData(e.target.value, 0)}
                        sx={{ minWidth: 90 }}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={75}>75</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </Select>
                    </Stack>
                    <Pagination
                    count={Math.max(1, Math.ceil((absentPagination.total || 0) / absentPagination.limit))}
                    page={absentPagination.current_page}
                    onChange={(e, page) => {
                        const newOffset = (page - 1) * absentPagination.limit;
                        fetchAbsentData(absentPagination.limit, newOffset);
                    }}
                    showFirstButton
                    showLastButton
                    />
                </Box>
            </Box>
            )}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Container>
  );
};

export default Unavailable;
